const DB_NAME = 'avoclima-offline';
const DB_VERSION = 2;
const STORE_NAME = 'queue';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('status', 'status', { unique: false });
        store.createIndex('createdAt', 'createdAt', { unique: false });
        store.createIndex('type', 'type', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// Enqueue a regular (non-photo) request
export async function enqueue(url, options = {}) {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);
  const entry = {
    url,
    method: options.method || 'GET',
    headers: options.headers ? JSON.parse(JSON.stringify(options.headers)) : {},
    body: options.body instanceof FormData ? null : options.body,
    hasFormData: options.body instanceof FormData,
    type: 'api',
    status: 'pending',
    createdAt: new Date().toISOString(),
    retries: 0,
  };
  return new Promise((resolve, reject) => {
    const req = store.add(entry);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

// Enqueue a photo upload (blob storage)
export async function enqueuePhoto(treeId, file, angle, deviceOrientation = {}, deviceMotion = {}, gpsSpeed = null) {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);

  // Convert File to ArrayBuffer for IndexedDB storage
  const buffer = await file.arrayBuffer();

  const entry = {
    url: `/api/v1/trees/${treeId}/photo`,
    method: 'POST',
    type: 'photo',
    status: 'pending',
    createdAt: new Date().toISOString(),
    retries: 0,
    photoData: {
      blob: buffer,
      mimeType: file.type,
      fileName: file.name,
    },
    photoMeta: {
      angle,
      alpha: deviceOrientation.alpha,
      beta: deviceOrientation.beta,
      gamma: deviceOrientation.gamma,
      rot_rate_alpha: deviceMotion.rot_alpha,
      rot_rate_beta: deviceMotion.rot_beta,
      rot_rate_gamma: deviceMotion.rot_gamma,
      gps_speed: gpsSpeed,
    },
  };

  return new Promise((resolve, reject) => {
    const req = store.add(entry);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

export async function getQueue() {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readonly');
  const store = tx.objectStore(STORE_NAME);
  const items = await new Promise((resolve) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve([]);
  });
  db.close();
  return items;
}

export async function getPendingCount() {
  const items = await getQueue();
  return items.filter(i => i.status === 'pending').length;
}

export async function removeFromQueue(id) {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);
  store.delete(id);
  tx.oncomplete = () => db.close();
}

export async function markFailed(id) {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);
  const item = await new Promise((resolve) => {
    const req = store.get(id);
    req.onsuccess = () => resolve(req.result);
  });
  if (item) {
    item.status = 'failed';
    item.retries = (item.retries || 0) + 1;
    store.put(item);
  }
  tx.oncomplete = () => db.close();
}

// Replay all pending items (both API and photo)
export async function replayQueue(authFetch) {
  const items = await getQueue();
  const pending = items.filter(i => i.status === 'pending');

  for (const item of pending) {
    try {
      if (item.type === 'photo') {
        await replayPhoto(item, authFetch);
      } else if (!item.hasFormData) {
        const res = await authFetch(item.url, {
          method: item.method,
          headers: item.body ? { 'Content-Type': 'application/json', ...item.headers } : item.headers,
          body: item.body || undefined,
        });
        if (res.ok) {
          await removeFromQueue(item.id);
        } else {
          await markFailed(item.id);
        }
      }
    } catch {
      await markFailed(item.id);
    }
  }
}

async function replayPhoto(item, authFetch) {
  const { photoData, photoMeta } = item;
  if (!photoData || !photoData.blob) {
    await markFailed(item.id);
    return;
  }

  // Reconstruct the file from ArrayBuffer
  const blob = new Blob([photoData.blob], { type: photoData.mimeType || 'image/jpeg' });
  const file = new File([blob], photoData.fileName || 'photo.jpg', { type: photoData.mimeType || 'image/jpeg' });

  const formData = new FormData();
  formData.append('file', file);
  formData.append('angle', photoMeta.angle || 'front');
  if (photoMeta.alpha !== null && photoMeta.alpha !== undefined) formData.append('alpha', photoMeta.alpha);
  if (photoMeta.beta !== null && photoMeta.beta !== undefined) formData.append('beta', photoMeta.beta);
  if (photoMeta.gamma !== null && photoMeta.gamma !== undefined) formData.append('gamma', photoMeta.gamma);
  if (photoMeta.rot_rate_alpha !== null) formData.append('rot_rate_alpha', photoMeta.rot_rate_alpha);
  if (photoMeta.rot_rate_beta !== null) formData.append('rot_rate_beta', photoMeta.rot_rate_beta);
  if (photoMeta.rot_rate_gamma !== null) formData.append('rot_rate_gamma', photoMeta.rot_rate_gamma);
  if (photoMeta.gps_speed !== null) formData.append('gps_speed', photoMeta.gps_speed);

  try {
    const res = await fetch(item.url, {
      method: 'POST',
      body: formData,
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });
    if (res.ok) {
      await removeFromQueue(item.id);
    } else {
      await markFailed(item.id);
    }
  } catch {
    await markFailed(item.id);
  }
}

// Online/offline detection
export function createOnlineStatusHook(React) {
  return function useOnlineStatus() {
    const [online, setOnline] = React.useState(navigator.onLine);
    React.useEffect(() => {
      const go = () => setOnline(true);
      const goff = () => setOnline(false);
      window.addEventListener('online', go);
      window.addEventListener('offline', goff);
      return () => {
        window.removeEventListener('online', go);
        window.removeEventListener('offline', goff);
      };
    }, []);
    return online;
  };
}
